import { Component, OnInit } from '@angular/core';
import { RenderStateService } from 'render/services/render-state/render-state.service';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { BaseRenderComponent } from 'cms/workspace/workspace-generic-classes/base-render.component';
import { StoresService } from 'idt-ecommerce-api/stores/stores.service';
import { StoreSummary, StoreLanguage } from 'idt-ecommerce-api/stores/stores.models';
import { PieceSelectMarketSettings } from 'extensions/oy/piece-select-market/front/models/piece-select-market.models';
import { RenderSlugService } from 'render/services/render-slug.service';
import { PieceSelectMarketAnalyticsTransformer } from '../models/piece-select-market-analytics.transformer';
import { PieceSelectMarketAnalytics } from '../models/piece-select-market-analytics.models';
import { CmsAnalyticsService } from 'idt-analytics-api/cms-analytics.service';
import { CookieService } from 'render/services/render-cookies.service';
import { ActivatedRoute } from '@angular/router';
import { UrlsWebHelpers } from 'idt-ecommerce-api/util/urls-web.helpers';
import { RenderEventsService } from 'render/render-events/render-events.service';
import { RenderEvent } from 'render/render-events/render-events.model';

@Component({
  selector: 'piece-select-market-render',
  templateUrl: './piece-select-market-render.component.html',
  styleUrls: ['./piece-select-market-render.component.scss']
})
export class PieceSelectMarketRenderComponent
  extends BaseRenderComponent<PieceSelectMarketSettings>
  implements OnInit {

  public stores: StoreSummary[] = [];
  public languages: StoreLanguage[] = [];
  public selectedStore: StoreSummary | undefined;
  public selectedLanguage: StoreLanguage | undefined;
  public remember: boolean = false;
  public selectMarketAnalytics: PieceSelectMarketAnalytics = {};

  constructor(
    private storesService: StoresService,
    private renderSlugService: RenderSlugService,
    private renderStateService: RenderStateService,
    private cookieService: CookieService,
    private trackService: CmsAnalyticsService,
    private activatedRoute: ActivatedRoute,
    private renderEventsService: RenderEventsService,
  ) {
    super();
  }

  public ngOnInit(): void {
    this.setAnalytics();
    this.initStores().subscribe(() => {
      this.checkShareUrlAndSavedCountry();
    });
  }

  public initStores(): Observable<StoreSummary[]> {
    return this.storesService.stores().pipe(
      tap((stores: StoreSummary[]) => {
        this.stores = stores;
        if (stores.length > 0 && this.country) {
          this.selectedStore = stores.find((store) => store.countryCode === this.country);
        }
      })
    );
  }

  public selectCanary() {
    const canaryIslands = this.stores.find((store) => store.countryCode === 'IC');
    if (canaryIslands) {
      this.selectedStore = canaryIslands;
      this.selectedLanguage = canaryIslands.supportedLanguages[0];
      this.goToHome();
    }
  }

  public goToHome() {
    if (isDefined(this.selectedStore) && isDefined(this.selectedLanguage)) {
      if (this.remember) {
        this.cookieService.set(`LAST_STORE_GEOBLOCKING={"storeId":${this.selectedStore.id},"langId":${this.selectedLanguage.id}}`);
      }
      const slug = this.renderSlugService.getSlug();
      const payload = {
        country: this.selectedStore.countryCode,
        code: this.selectedLanguage.code
      };
      this.renderStateService.setStoreInfo(slug, payload);

      // Si se ha llegado con el param shareUrl navegamos a esa ruta, sino a la Home del país
      const shareUrl: string | null = this.activatedRoute.snapshot.queryParamMap.get('shareUrl');
      if (shareUrl) {
        // Navegamos a esta ruta con el store marcado
        this.navigateToShareUrlForStore(shareUrl, this.selectedStore);
      } else {
        const renderEvent = {
          extra: {
            type: 'languageChange',
            country: this.selectedStore.countryCode,
            lang: this.selectedLanguage.code,
          },
        } as RenderEvent;
        this.renderEventsService.events.next(renderEvent);
      }

      if (this.selectMarketAnalytics?.preselectRegion) {
        this.trackService.track(this.selectMarketAnalytics.preselectRegion, { country: this.selectedStore.countryName.toLowerCase() });
      }
      if (this.selectMarketAnalytics?.preselectLanguage) {
        this.trackService.track(this.selectMarketAnalytics.preselectLanguage, { language: this.selectedLanguage.name.toLowerCase() });
      }
      if (this.selectMarketAnalytics?.accessMarket) {
        this.trackService.track(this.selectMarketAnalytics.accessMarket, {});
      }
    }
  }

  public rememberMarket(event: any) {
    this.remember = event?.target?.value === 'on';
  }

  public onMarketChange(event: { store: StoreSummary; lang: StoreLanguage }) {
    this.selectedStore = event.store;
    this.selectedLanguage = event.lang;
  }

  public trackByIndex(index: number): number {
    return index;
  }

  private setAnalytics() {
    if (this.settings?.analytics?.events) {
      this.selectMarketAnalytics = PieceSelectMarketAnalyticsTransformer.setSelectMarketAnalytics(this.settings?.analytics?.events);
    }
  }

  /**
   * Revisa si hemos llegado con shareUrl y tenemos la cookie LAST_STORE_GEOBLOCKING
   */
  private checkShareUrlAndSavedCountry() {
    const shareUrl: string | null = this.activatedRoute.snapshot.queryParamMap.get('shareUrl');
    const lastStoreGeoBlocking: string | undefined = this.cookieService.getCookie('LAST_STORE_GEOBLOCKING');

    if (lastStoreGeoBlocking && shareUrl) {
      const lastStoreParsed = JSON.parse(lastStoreGeoBlocking);
      if (lastStoreParsed?.storeId) {
        const lastStore = this.stores.find((store) => Number(store.id) === Number(lastStoreParsed.storeId));
        if (lastStore) {
          this.navigateToShareUrlForStore(shareUrl, lastStore);
        }
      }
    }
  }

  /**
   * Recibe el query param shareUrl y la tienda actual para navegar a la WW correspondiente
   * @param {string} shareUrl - The shareUrl query param retrieved from the URL
   * @param {StoreSummary} store - Info of the current store
   */
  private navigateToShareUrlForStore(shareUrl: string, store: StoreSummary) {
    const newUrl: string | undefined = UrlsWebHelpers.getShareUrlForStore(shareUrl, store);
    if (newUrl) {
      const renderEvent = {
        extra: {},
        location: newUrl,
      } as RenderEvent;
      this.renderEventsService.events.next(renderEvent);
    }
  }

}
